<template lang="pug">
	.about-us
		//- Primary section
		section.primary-section
			.primary-section__logo
				img.lazyload(src='/img/img-preloader.svg'
											:data-src="primarySection.logo" 
											:alt="$t('pages.aboutUs.imageAlt')"
											:title="$t('pages.aboutUs.imageAlt')")
			.primary-section__title {{ primarySection.title }}

		//- Features section
		section.features-section
			.features-block(v-for="(block, index) in featuresSection" :key="index"
											:class="{ big: block.is_big }")
				.features-block__row
					.features-block__photo(:class="{ left: block.photo_placement === 'left', right: block.photo_placement === 'right' }")
						img.lazyload(src='/img/img-preloader.svg'
													:data-src="block.photo" 
													:alt="$t('pages.aboutUs.imageAltNumber') + (index + 1)")
					.features-block__content
						h3.title {{ block.title }}
						.descr
							p {{ block.descr }}

		//- Story section
		section.story-section
			.story-section__content(v-html="page.content.rendered")

		//- Benefits section
		section.benefits-section
			.benefits-section__row
				.benefits-section__photo
					img.lazyload(src='/img/img-preloader.svg'
												:data-src="benefitsSection.photo" 
												:alt="$t('pages.aboutUs.imageBenefitsAlt')"
												:title="$t('pages.aboutUs.imageBenefitsAlt')")
				.benefits-section__content
					h2.title {{ benefitsSection.title }}
					.list(v-html="benefitsSection.list")
					.signature {{ benefitsSection.signature }}
				.benefits-section__mobile
					.signature {{ benefitsSection.signature }}

		//- Categories section
		CategoriesSection(:isFrontPage="false")
</template>

<script>
import api from '@/api/index'
import CategoriesSection from '@/components/partials/CategoriesSection'

export default {
  layout: 'no-sidebar',

  components: { CategoriesSection },

  data() {
    return {
      page: {}
    }
  },

  head() {
    return {
      title: this.pageTitle,
      titleTemplate: null,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: this.metaDescr
        },
        {
          hid: 'og:title',
          property: 'og:title',
          content: this.pageTitle
        },
        {
          hid: 'og:description',
          property: 'og:description',
          content: this.metaDescr
        }
			],
    }
  },

  computed: {
    pageTitle() {
      return this.page.yoast_meta.yoast_wpseo_title
    },

    metaDescr() {
      return this.page.yoast_meta.yoast_wpseo_metadesc
    },

    primarySection() {
      return this.page.acf.primary_section
    },

    featuresSection() {
      return this.page.acf.section
    },

    benefitsSection() {
      return this.page.acf.benefits
    }
  },

  async asyncData({ store, params }) {
    const data = await api.getAboutPage(store.state.geoData.locale)

    return { page: data }
  }
}
</script>

<style lang="scss">
@import '~assets/scss/helpers/vars';
@import '~assets/scss/helpers/mixins';

.about-us {
  letter-spacing: -0.3px;

  .primary-section {
    padding: 120px 0;
    text-align: center;

    @include laptop {
      padding: 90px 0;
    }

    @include tablet {
      padding: 75px 0;
    }

    @include mobile {
      padding: 60px 0;
    }

    &__logo {
      max-width: 340px;
      margin: 0 auto 20px;

      @include laptop {
        max-width: 300px;
      }

      @include tablet {
        max-width: 275px;
      }

      @include mobile {
        max-width: 250px;
      }
    }

    &__title {
      font-size: 18px;
      font-weight: 500;
      letter-spacing: 0.75px;
      line-height: 1.33;
      text-transform: uppercase;

      @include laptop {
        font-size: 16px;
      }

      @include tablet {
        font-size: 14px;
      }

      @include mobile {
        font-size: 12px;
      }
    }
  }

  .features-section {
    background-color: #fffefb;
  }

  .features-block {
    &__row {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
    }

    &__photo,
    &__content {
      flex: 0 0 100%;
      max-width: 50%;

      @include mobile {
        max-width: 100%;
      }
    }

    &__photo {
      &.right {
        order: 1;

        @include mobile {
          order: unset;
        }
      }
    }

    &__content {
      text-align: center;

      @include mobile {
        order: -1;
        min-height: 250px;
        padding: 60px 0;
      }

      .title {
        margin-bottom: 30px;
        color: #d8433c;
        font-size: 18px;
        font-weight: 600;
        letter-spacing: 0.72px;
        text-transform: uppercase;

        @include tablet {
          margin-bottom: 20px;
        }
      }

      .descr {
        max-width: 480px;
        margin: 0 auto;
        font-size: 20px;
        font-weight: 500;
        line-height: 1.5;

        @include laptop {
          max-width: 300px;
        }

        @include tablet {
          font-size: 16px;
        }
      }
    }

    // Big block
    &.big {
      // min-height: 820px;

      @include desktop {
        min-height: unset;
      }

      .features-block__content {
        @include mobile {
          order: -1;
        }

        .title {
          margin-bottom: 100px;
          font-size: 18px;
          letter-spacing: 2.18px;

          @include laptop {
            margin-bottom: 30px;
            font-size: 16px;
          }

          @include tablet {
            margin-bottom: 20px;
            font-size: 14px;
          }
        }

        .descr {
          max-width: 330px;
          font-size: 38px;
          line-height: 1.47;
          letter-spacing: 1.58px;
          text-transform: uppercase;

          @include laptop {
            font-size: 28px;
          }

          @include tablet {
            font-size: 24px;
          }
        }
      }
    }
  }

  .story-section {
    padding: 120px 0;
    font-size: 16px;
    line-height: 1.63;
    text-align: center;

    @include laptop {
      padding: 75px 0;
    }

    @include tablet {
      padding: 60px 0;
    }

    .story-section__content {
      max-width: 880px;
      margin: 0 auto;
    }

    h1,
		h2 {
      margin-bottom: 50px;
      color: $blueColor;
      font-size: 32px;
      font-weight: 600;
      line-height: 1.35;
      letter-spacing: 2.67px;
      text-transform: uppercase;

      @include laptop {
        margin-bottom: 30px;
        font-size: 28px;
      }

      @include tablet {
        margin-bottom: 20px;
        font-size: 24px;
      }

      @include mobile {
        font-size: 20px;
      }
    }

    p {
      margin-bottom: 30px;

      @include laptop {
        margin-bottom: 20px;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .benefits-section {
    padding-bottom: 120px;

    @include laptop {
      padding-bottom: 75px;
    }

    @include tablet {
      padding-bottom: 60px;
    }

    &__row {
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
    }

    &__photo {
      flex: 0 0 100%;
      max-width: 480px;
      margin-right: 15px;

      @include laptop {
        max-width: calc(50% - 15px);
      }

      @include mobile {
        max-width: 100%;
        margin-right: 0;
      }
    }

    &__content {
      display: flex;
      flex-direction: column;
      flex: 0 0 100%;
      max-width: 580px;

      @include laptop {
        max-width: 50%;
      }

      @include mobile {
        order: -1;
        max-width: 100%;
        margin-bottom: 40px;
      }

      .title {
        margin-bottom: 40px;
        font-size: 32px;
        font-weight: 600;
        letter-spacing: 1.45px;
        line-height: 1.69;
        text-transform: uppercase;

        @include laptop {
          margin-bottom: 30px;
          font-size: 28px;
        }

        @include tablet {
          margin-bottom: 25px;
          font-size: 24px;
        }

        @include mobile {
          font-size: 20px;
          text-align: center;
        }
      }

      .list {
        font-size: 21px;
        line-height: 1.62;

        @include laptop {
          font-size: 19px;
        }

        @include tablet {
          font-size: 16px;
        }

        ul {
          li {
            position: relative;
            padding-left: 40px;
            margin-bottom: 15px;

            @include tablet {
              padding-left: 25px;
            }

            &:before {
              content: '';
              position: absolute;
              top: 10px;
              left: 0;
              display: block;
              width: 10px;
              height: 10px;
              background-color: #ffc200;
              border-radius: 50%;

              @include tablet {
                top: 5px;
              }
            }

            &:last-child {
              margin-bottom: 0;
            }
          }
        }
      }

      .signature {
        margin-top: auto;
        color: rgba($blackColor, 0.85);
        font-weight: 500;
        font-style: italic;
        letter-spacing: 0.6px;
        line-height: 1.9;

        @include tablet {
          font-size: 12px;
        }

        @include mobile {
          display: none;
        }
      }
    }

    &__mobile {
      display: none;

      @include mobile {
        display: block;
        margin-top: 10px;
      }

      .signature {
        color: rgba($blackColor, 0.85);
        font-size: 12px;
        font-weight: 500;
        font-style: italic;
        letter-spacing: 0.6px;
        line-height: 1.4;
      }
    }
  }
}
</style>
